import React, { useEffect, useState, useCallback } from 'react';
import { apiGet, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { Ticket, X, UserCheck, CheckCircle2 } from 'lucide-react';
import EmployeeSelect from '../../components/pickers/EmployeeSelect';

const PRIORITY_COLORS = {
  low: 'bg-slate-100 text-slate-600', medium: 'bg-blue-50 text-blue-700',
  high: 'bg-amber-50 text-amber-700', urgent: 'bg-red-50 text-red-700 border border-red-200',
};
const STATUS_COLORS = {
  open: 'bg-amber-50 text-amber-700', in_progress: 'bg-blue-50 text-blue-700',
  resolved: 'bg-emerald-50 text-emerald-700', closed: 'bg-slate-100 text-slate-500',
};

export default function ITServiceRequests() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState({ status: '', priority: '' });
  const [assignFor, setAssignFor] = useState(null);
  const [resolveFor, setResolveFor] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (filter.status) params.status = filter.status;
    if (filter.priority) params.priority = filter.priority;
    apiGet('/it/requests', params).then((r) => { setRows(r.items || []); setCounts(r.counts || {}); }).catch(() => toast.error('Failed to load (need IT permission)'));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="it-service-requests">
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Information Technology</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Service Requests</h1>
        <p className="text-sm text-slate-600 mt-1">Hardware / software / access tickets from employees.</p>
      </div>

      <div className="grid grid-cols-3 gap-3 max-w-md">
        <div className="rounded-sm border border-slate-200 p-3 bg-white"><div className="text-[10px] text-slate-500 font-bold uppercase">Open</div><div className="text-lg font-display font-extrabold text-[#3b4872]">{counts.open || 0}</div></div>
        <div className="rounded-sm border border-slate-200 p-3 bg-white"><div className="text-[10px] text-slate-500 font-bold uppercase">In Progress</div><div className="text-lg font-display font-extrabold text-[#3b4872]">{counts.in_progress || 0}</div></div>
        <div className={`rounded-sm border p-3 ${counts.urgent_open ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'}`}><div className="text-[10px] text-slate-500 font-bold uppercase">Urgent Open</div><div className="text-lg font-display font-extrabold text-red-600">{counts.urgent_open || 0}</div></div>
      </div>

      <div className="flex flex-wrap gap-2">
        <select className="border border-slate-200 rounded-sm px-3 py-1.5 text-xs" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} data-testid="req-filter-status">
          <option value="">All statuses</option>{Object.keys(STATUS_COLORS).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="border border-slate-200 rounded-sm px-3 py-1.5 text-xs" value={filter.priority} onChange={(e) => setFilter({ ...filter, priority: e.target.value })} data-testid="req-filter-priority">
          <option value="">All priorities</option>{Object.keys(PRIORITY_COLORS).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="requests-empty"><Ticket size={40} className="mx-auto mb-3 opacity-40" /><p className="text-sm">No service requests.</p></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500"><tr>
              {['Ticket', 'Title', 'Type', 'Priority', 'Raised By', 'Assigned', 'Status', 'Actions'].map((h) => <th key={h} className="text-left px-3 py-2 font-display font-bold uppercase tracking-wider text-[10px]">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`req-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono text-[11px]">{r.request_no}</td>
                  <td className="px-3 py-2"><div className="font-display font-bold">{r.title}</div><div className="text-slate-400 text-[10px]">{r.description || ''}</div></td>
                  <td className="px-3 py-2 text-slate-600">{(r.request_type || '').replace(/_/g, ' ')}</td>
                  <td className="px-3 py-2"><span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-sm ${PRIORITY_COLORS[r.priority]}`}>{r.priority}</span></td>
                  <td className="px-3 py-2 text-slate-600">{r.requested_by_name || '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{r.assigned_to_name || <span className="text-slate-300">unassigned</span>}</td>
                  <td className="px-3 py-2"><span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-sm ${STATUS_COLORS[r.status]}`}>{(r.status || '').replace(/_/g, ' ')}</span></td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-3">
                      {['open', 'in_progress'].includes(r.status) && <button onClick={() => setAssignFor(r)} data-testid={`assign-${r.id}`} className="text-[#3b4872] font-bold flex items-center gap-1"><UserCheck size={12} /> Assign</button>}
                      {['open', 'in_progress'].includes(r.status) && <button onClick={() => setResolveFor(r)} data-testid={`resolve-${r.id}`} className="text-emerald-700 font-bold flex items-center gap-1"><CheckCircle2 size={12} /> Resolve</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {assignFor && <AssignModal req={assignFor} onClose={() => setAssignFor(null)} onDone={() => { setAssignFor(null); load(); }} />}
      {resolveFor && <ResolveModal req={resolveFor} onClose={() => setResolveFor(null)} onDone={() => { setResolveFor(null); load(); }} />}
    </div>
  );
}

function AssignModal({ req, onClose, onDone }) {
  const [emp, setEmp] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!emp) { toast.error('Select an IT staff member'); return; }
    setBusy(true);
    try { await apiPut(`/it/requests/${req.id}/assign`, { assigned_to_it_staff_id: emp.id }); toast.success('Assigned'); onDone(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Assign failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="assign-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-sm p-5 space-y-3">
        <div className="flex items-center justify-between"><h3 className="font-display font-bold text-sm">Assign — {req.request_no}</h3><button onClick={onClose}><X size={18} /></button></div>
        <div><span className="lbl">IT Staff *</span><EmployeeSelect value={emp} onSelect={setEmp} testid="assign-emp" /></div>
        <button onClick={submit} disabled={busy} data-testid="assign-submit" className="w-full py-2 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider disabled:opacity-50">{busy ? 'Assigning…' : 'Assign'}</button>
        <style>{`.lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;display:block;margin-bottom:4px}`}</style>
      </div>
    </div>
  );
}

function ResolveModal({ req, onClose, onDone }) {
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('resolved');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await apiPut(`/it/requests/${req.id}/resolve`, { resolution_notes: notes, status }); toast.success('Updated'); onDone(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="resolve-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-sm p-5 space-y-3">
        <div className="flex items-center justify-between"><h3 className="font-display font-bold text-sm">Resolve — {req.request_no}</h3><button onClick={onClose}><X size={18} /></button></div>
        <label className="block"><span className="lbl">Resolution Notes</span><textarea rows={3} className="inp" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="resolve-notes" /></label>
        <label className="block"><span className="lbl">Status</span><select className="inp" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="resolve-status"><option value="resolved">Resolved</option><option value="closed">Closed</option></select></label>
        <button onClick={submit} disabled={busy} data-testid="resolve-submit" className="w-full py-2 bg-emerald-700 text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider disabled:opacity-50">{busy ? 'Saving…' : 'Mark ' + status}</button>
        <style>{`.inp{width:100%;border:1px solid #e2e8f0;border-radius:2px;padding:8px 10px;font-size:13px;outline:none}.inp:focus{border-color:#3b4872}.lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;display:block;margin-bottom:4px}`}</style>
      </div>
    </div>
  );
}
